import arrowLeftAlt from '@iconify/icons-material-symbols/arrow-left-alt';
import arrowRightAlt from '@iconify/icons-material-symbols/arrow-right-alt';
import calendarMonthRounded from '@iconify/icons-material-symbols/calendar-month-rounded';
import warningIcon from '@iconify/icons-material-symbols/warning-outline';
import { parseDate } from '@internationalized/date';
import type { InputHTMLAttributes } from 'react';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { tv, type VariantProps } from '#pie/utils/TailwindUtils';
import { Icon } from '../icon/Icon';
import { styles } from '../input-text/InputText';

export interface InputDateProps
  extends Omit<VariantProps<typeof inputDate>, 'hasIcon'>,
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'disabled' | 'onChange' | 'value'
    > {
  onChange: (date: string) => void;
  /** Undefined will be converted to null to keep the underlying DatePicker controlled */
  value?: string;
  disableFutureDates?: boolean;
}

export const InputDate = function InputDate({
  ref,
  className,
  isError: externalIsError,
  disabled,
  onChange,
  value,
  disableFutureDates = false,
  ...props
}: InputDateProps & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  let datePickerValue = null;
  let parseError = false;

  try {
    datePickerValue = value ? parseDate(value) : null;
  } catch (e: unknown) {
    parseError = true;
    console.error('Parse error:', (e as Error).message);
  }

  const { t } = useTranslation('common');

  const isFutureDate = (date: any) => {
    if (!disableFutureDates) return false;

    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return new Date(date.year, date.month - 1, date.day) > today;
  };

  const isInvalidFutureDate =
    datePickerValue && disableFutureDates
      ? isFutureDate(datePickerValue)
      : false;

  const errorMessage = isInvalidFutureDate
    ? t('zod.errors.future.date.invalid')
    : undefined;

  const isError = externalIsError || parseError || isInvalidFutureDate;

  const s = inputDate({ disabled, isError });

  return (
    <div className="w-full">
      <DatePicker
        aria-label={props['aria-label']}
        aria-labelledby={props.id}
        className={s.input({ className })}
        isDisabled={disabled}
        onChange={(date) => {
          /* istanbul ignore if -- @preserve*/
          if (date === null) {
            onChange?.('');
            return;
          }
          onChange?.(date.toString());
        }}
        ref={ref}
        value={datePickerValue}
      >
        <Group className="flex w-full items-center justify-between px-4 py-2.75">
          <DateInput className="flex">
            {(segment) => <DateSegment segment={segment} />}
          </DateInput>

          <Button
            isDisabled={disabled}
            className="ml-auto disabled:cursor-not-allowed"
            aria-label={t('input_date.date_picker.aria-label')}
            id={props.id || props.name}
          >
            <Icon icon={calendarMonthRounded} size={16} />
          </Button>
        </Group>
        <Popover>
          <Dialog>
            <Calendar className="w-full rounded-md bg-white px-4 py-2 shadow-pop-over">
              <header className="flex justify-between px-1 py-2 text-xl font-bold">
                <Button
                  slot="previous"
                  aria-label={t(
                    'input_date.date_picker_previous_month.aria-label',
                  )}
                >
                  <Icon icon={arrowLeftAlt} size={16} />
                </Button>

                <Heading
                  aria-label={t(
                    'input_date.date_picker_current_month_year.aria-label',
                  )}
                />

                <Button
                  slot="next"
                  aria-label={t('input_date.date_picker_next_month.aria-label')}
                >
                  <Icon icon={arrowRightAlt} size={16} />
                </Button>
              </header>
              <CalendarGrid>
                {(date) => {
                  const isFuture = isFutureDate(date);

                  return (
                    <CalendarCell
                      aria-label={date.toString()}
                      className={`
                        p-2 text-center focus:outline-hidden
                        data-[selected=true]:rounded-sm data-[selected=true]:text-typography-success data-[selected=true]:bg-background-minimal-teal
                        data-[disabled=true]:hover:cursor-default hover:bg-background-minimal-teal
                        ${
                          isFuture && disableFutureDates
                            ? 'text-typography-error bg-surface-error hover:bg-surface-error hover:text-content-reversed hover:rounded-xs focus:bg-surface-error focus:rounded-xs data-[selected=true]:bg-surface-error'
                            : 'hover:bg-secondary-light hover:rounded-xs focus:bg-secondary-light focus:rounded-xs data-[selected=true]:bg-secondary'
                        }
                      `}
                      date={date}
                    />
                  );
                }}
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Popover>
      </DatePicker>
      {errorMessage && (
        <div className="mt-1 text-base text-typography-error" role="alert">
          <Icon icon={warningIcon} size={16} /> {errorMessage}
        </div>
      )}
    </div>
  );
};

export const inputDate = tv({
  base: 'flex w-fit items-center gap-2',
  extend: styles,
  slots: {
    input:
      'flex items-center gap-4 p-0 rounded-xs border border-border-input-enabled w-full data-[open=true]:border-border-input-active',
  },
  variants: {
    isError: {
      true: {
        input: 'border-support-error-bold focus:border-support-error-bold',
      },
    },
  },
});
